import { isCanonicalCalculatorCreatedAtUtc } from "../calculator-user-layer-v4-values.js";
import { isCalculatorGroupRoleV1 } from "../calculator-group-role-v1.js";
import {
    canonicalizeCalculatorFishingSourceV1,
} from "./calculator-fishing-source.js";

const U32_MAX = 4_294_967_295;
const FACT_ID_PATTERN = /^blake3:[0-9a-f]{64}$/;
const RATE_BASES = new Set([
    "ratio",
    "percent",
    "denormalizedWeight",
    "rawWeight",
    "perCatch",
    "perHour",
]);
const NUMERIC_UNITS = new Set([
    "seconds",
    "count",
    "perHour",
    "silver",
    "experience",
    "percent",
    "ratio",
    "multiplier",
    "durability",
    "days",
]);
const PRESENCE_VALUES = new Set(["present", "absent", "unknown"]);
const MEMBERSHIP_VALUES = new Set(["member", "notMember", "unknown"]);
const UNAVAILABLE_REASON_CODES = new Set([
    "missingSourceFact",
    "unsupportedSourceVariant",
    "noEquivalentRawWeight",
    "incompleteProvenance",
    "notApplicable",
    "modelSelectionRequired",
]);
const OPERATION_CONFIDENCE_VALUES = new Set(["known", "guessed"]);

function isRecord(value) {
    return Boolean(value)
        && typeof value === "object"
        && !Array.isArray(value)
        && Object.prototype.toString.call(value) === "[object Object]";
}

function exactObject(value, fields, path, optionalFields = []) {
    if (!isRecord(value)) {
        throw new CalculatorLayerInspectorDraftError(path, "expected an object");
    }
    const allowed = new Set([...fields, ...optionalFields]);
    for (const field of Object.keys(value)) {
        if (!allowed.has(field)) {
            throw new CalculatorLayerInspectorDraftError(`${path}.${field}`, "unknown field");
        }
    }
    for (const field of fields) {
        if (!Object.prototype.hasOwnProperty.call(value, field)) {
            throw new CalculatorLayerInspectorDraftError(`${path}.${field}`, "missing field");
        }
    }
    return value;
}

function parseDraftMetadata(value, path) {
    if (value === undefined) {
        return undefined;
    }
    const metadata = exactObject(
        value,
        ["confidence", "author", "createdAtUtc"],
        path,
    );
    if (!OPERATION_CONFIDENCE_VALUES.has(metadata.confidence)) {
        throw new CalculatorLayerInspectorDraftError(
            `${path}.confidence`,
            "expected known or guessed",
        );
    }
    const author = exactObject(
        metadata.author,
        ["id", "displayName", "handle"],
        `${path}.author`,
    );
    const createdAtUtc = nonEmptyString(
        metadata.createdAtUtc,
        `${path}.createdAtUtc`,
    );
    if (!isCanonicalCalculatorCreatedAtUtc(createdAtUtc)) {
        throw new CalculatorLayerInspectorDraftError(
            `${path}.createdAtUtc`,
            "expected a valid canonical YYYY-MM-DDTHH:mm:ss.sssZ timestamp",
        );
    }
    return {
        confidence: metadata.confidence,
        author: {
            id: nonEmptyString(author.id, `${path}.author.id`),
            displayName: nonEmptyString(
                author.displayName,
                `${path}.author.displayName`,
            ),
            handle: nonEmptyString(author.handle, `${path}.author.handle`),
        },
        createdAtUtc,
    };
}

function operationConfidence(value, path) {
    if (!OPERATION_CONFIDENCE_VALUES.has(value)) {
        throw new CalculatorLayerInspectorDraftError(
            path,
            "expected known or guessed",
        );
    }
    return value;
}

function nonEmptyString(value, path) {
    if (typeof value !== "string" || !value.trim() || value !== value.trim()) {
        throw new CalculatorLayerInspectorDraftError(
            path,
            "expected a non-empty string without surrounding whitespace",
        );
    }
    return value;
}

function sourceExpressionString(value, path) {
    if (typeof value !== "string" || !value.trim()) {
        throw new CalculatorLayerInspectorDraftError(
            path,
            "expected a non-blank source expression",
        );
    }
    return value;
}

function optionalString(value, path) {
    return value === null ? null : nonEmptyString(value, path);
}

function optionalComment(value, path) {
    if (value === null) {
        return null;
    }
    const comment = nonEmptyString(value, path);
    if ([...comment].length > 2_000) {
        throw new CalculatorLayerInspectorDraftError(
            path,
            "operation comment must contain at most 2000 characters",
        );
    }
    return comment;
}

function itemId(value, path) {
    if (!Number.isInteger(value) || value < 1 || value > U32_MAX) {
        throw new CalculatorLayerInspectorDraftError(
            path,
            `expected an integer from 1 through ${U32_MAX}`,
        );
    }
    return value;
}

function fishingSource(value, path) {
    return canonicalizeCalculatorFishingSourceV1(
        value,
        path,
        (issuePath, _code, message) => {
            throw new CalculatorLayerInspectorDraftError(issuePath, message);
        },
    );
}

function nonNegativeNumber(value, path) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        throw new CalculatorLayerInspectorDraftError(
            path,
            "expected a finite non-negative number",
        );
    }
    return Object.is(value, -0) ? 0 : value;
}

function deepFreeze(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) {
        return value;
    }
    for (const child of Object.values(value)) {
        deepFreeze(child);
    }
    return Object.freeze(value);
}

function clonePlainValue(value, path) {
    if (Array.isArray(value)) {
        return value.map((entry, index) => clonePlainValue(entry, `${path}[${index}]`));
    }
    if (isRecord(value)) {
        return Object.fromEntries(Object.entries(value).map(([key, child]) => [
            key,
            clonePlainValue(child, `${path}.${key}`),
        ]));
    }
    if (
        value === null
        || typeof value === "string"
        || typeof value === "boolean"
        || (typeof value === "number" && Number.isFinite(value))
    ) {
        return value;
    }
    throw new CalculatorLayerInspectorDraftError(path, "expected JSON-compatible data");
}

function parseUnavailableReason(value, path) {
    const reason = exactObject(value, ["code", "sourceId", "detailCode"], path);
    if (!UNAVAILABLE_REASON_CODES.has(reason.code)) {
        throw new CalculatorLayerInspectorDraftError(`${path}.code`, "unsupported reason code");
    }
    return {
        code: reason.code,
        sourceId: optionalString(reason.sourceId, `${path}.sourceId`),
        detailCode: optionalString(reason.detailCode, `${path}.detailCode`),
    };
}

function parseRate(value, path) {
    if (!isRecord(value)) {
        throw new CalculatorLayerInspectorDraftError(path, "expected a typed rate");
    }
    if (value.state === "known") {
        const rate = exactObject(value, ["state", "value", "basis"], path);
        if (!RATE_BASES.has(rate.basis)) {
            throw new CalculatorLayerInspectorDraftError(`${path}.basis`, "unsupported rate basis");
        }
        return {
            state: "known",
            value: nonNegativeNumber(rate.value, `${path}.value`),
            basis: rate.basis,
        };
    }
    if (value.state === "unknown") {
        exactObject(value, ["state"], path);
        return { state: "unknown" };
    }
    if (value.state === "unavailable") {
        const rate = exactObject(value, ["state", "reason"], path);
        return {
            state: "unavailable",
            reason: parseUnavailableReason(rate.reason, `${path}.reason`),
        };
    }
    throw new CalculatorLayerInspectorDraftError(`${path}.state`, "unsupported rate state");
}

function parseDropRate(value, path) {
    const rate = parseRate(value, path);
    if (rate.state === "known") {
        if (
            rate.basis !== "rawWeight"
            && rate.basis !== "denormalizedWeight"
            && rate.basis !== "percent"
        ) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.basis`,
                "known user loot rates must use rawWeight, denormalizedWeight, or percent basis",
            );
        }
        if (rate.basis === "percent" && rate.value > 100) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.value`,
                "effective percentage must be at most 100",
            );
        }
    }
    return rate;
}

function parseItemQuantity(value, path) {
    if (!isRecord(value)) {
        throw new CalculatorLayerInspectorDraftError(path, "expected a typed item quantity");
    }
    if (value.state === "known") {
        const quantity = exactObject(value, ["state", "minimum", "maximum"], path);
        const minimum = quantity.minimum;
        const maximum = quantity.maximum;
        for (const [field, bound] of [["minimum", minimum], ["maximum", maximum]]) {
            if (!Number.isInteger(bound) || bound < 0 || bound > U32_MAX) {
                throw new CalculatorLayerInspectorDraftError(
                    `${path}.${field}`,
                    `expected an integer from 0 through ${U32_MAX}`,
                );
            }
        }
        if (maximum < minimum) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.maximum`,
                "must be at least the minimum",
            );
        }
        return { state: "known", minimum, maximum };
    }
    if (value.state === "unknown") {
        exactObject(value, ["state"], path);
        return { state: "unknown" };
    }
    if (value.state === "unavailable") {
        const quantity = exactObject(value, ["state", "reason"], path);
        return {
            state: "unavailable",
            reason: parseUnavailableReason(quantity.reason, `${path}.reason`),
        };
    }
    throw new CalculatorLayerInspectorDraftError(
        `${path}.state`,
        "unsupported item quantity state",
    );
}

function parseNumericValue(value, path) {
    if (!isRecord(value)) {
        throw new CalculatorLayerInspectorDraftError(path, "expected a typed numeric value");
    }
    if (value.state === "known") {
        const numeric = exactObject(value, ["state", "value", "unit"], path);
        if (!NUMERIC_UNITS.has(numeric.unit)) {
            throw new CalculatorLayerInspectorDraftError(`${path}.unit`, "unsupported numeric unit");
        }
        return {
            state: "known",
            value: nonNegativeNumber(numeric.value, `${path}.value`),
            unit: numeric.unit,
        };
    }
    if (value.state === "unknown") {
        exactObject(value, ["state"], path);
        return { state: "unknown" };
    }
    if (value.state === "unavailable") {
        const numeric = exactObject(value, ["state", "reason"], path);
        return {
            state: "unavailable",
            reason: parseUnavailableReason(numeric.reason, `${path}.reason`),
        };
    }
    throw new CalculatorLayerInspectorDraftError(`${path}.state`, "unsupported numeric state");
}

function parseRateContext(value, path) {
    const context = exactObject(value, ["conditions", "branchId", "alternative"], path);
    if (!Array.isArray(context.conditions)) {
        throw new CalculatorLayerInspectorDraftError(`${path}.conditions`, "expected an array");
    }
    return {
        conditions: clonePlainValue(context.conditions, `${path}.conditions`),
        branchId: optionalString(context.branchId, `${path}.branchId`),
        alternative: parseRateAlternativeContext(
            context.alternative,
            `${path}.alternative`,
        ),
    };
}

function parseRateAlternativeContext(value, path) {
    if (value === null) {
        return null;
    }
    const alternative = exactObject(
        value,
        ["familyId", "alternativeId", "priority", "applicability"],
        path,
    );
    if (
        !Number.isInteger(alternative.priority)
        || alternative.priority < 0
        || alternative.priority > U32_MAX
    ) {
        throw new CalculatorLayerInspectorDraftError(
            `${path}.priority`,
            `expected an integer from 0 through ${U32_MAX}`,
        );
    }
    const applicabilityPath = `${path}.applicability`;
    const applicability = alternative.applicability;
    let parsedApplicability;
    if (applicability?.kind === "fallback") {
        exactObject(applicability, ["kind"], applicabilityPath);
        parsedApplicability = { kind: "fallback" };
    } else if (applicability?.kind === "typedConditions") {
        const typed = exactObject(
            applicability,
            ["kind", "conditions"],
            applicabilityPath,
        );
        if (!Array.isArray(typed.conditions) || typed.conditions.length === 0) {
            throw new CalculatorLayerInspectorDraftError(
                `${applicabilityPath}.conditions`,
                "expected a non-empty array",
            );
        }
        parsedApplicability = {
            kind: "typedConditions",
            conditions: clonePlainValue(
                typed.conditions,
                `${applicabilityPath}.conditions`,
            ),
        };
    } else if (applicability?.kind === "sourceExpression") {
        const source = exactObject(
            applicability,
            ["kind", "expression"],
            applicabilityPath,
        );
        parsedApplicability = {
            kind: "sourceExpression",
            expression: sourceExpressionString(
                source.expression,
                `${applicabilityPath}.expression`,
            ),
        };
    } else {
        throw new CalculatorLayerInspectorDraftError(
            `${applicabilityPath}.kind`,
            "expected fallback, typedConditions, or sourceExpression",
        );
    }
    return {
        familyId: nonEmptyString(alternative.familyId, `${path}.familyId`),
        alternativeId: nonEmptyString(alternative.alternativeId, `${path}.alternativeId`),
        priority: alternative.priority,
        applicability: parsedApplicability,
    };
}

function validateUserLootRateContext(rate, context, path) {
    if (context.alternative !== null && context.branchId === null) {
        throw new CalculatorLayerInspectorDraftError(
            `${path}.context.branchId`,
            "rate alternatives require a non-null branch ID",
        );
    }
    if (context.alternative !== null && context.conditions.length > 0) {
        throw new CalculatorLayerInspectorDraftError(
            `${path}.context.conditions`,
            "rate alternative applicability owns conditions; direct conditions must be empty",
        );
    }
    if (
        rate.state === "known"
        && rate.basis === "percent"
        && context.branchId !== null
    ) {
        throw new CalculatorLayerInspectorDraftError(
            `${path}.context.branchId`,
            "known percent user loot rates must be branchless",
        );
    }
}

export class CalculatorLayerInspectorDraftError extends TypeError {
    constructor(path, message) {
        super(`Calculator layer inspector draft failed at ${path}: ${message}`);
        this.name = "CalculatorLayerInspectorDraftError";
        this.path = path;
    }
}

export function parseCalculatorLayerInspectorDraft(value) {
    if (!isRecord(value)) {
        throw new CalculatorLayerInspectorDraftError("$.draft", "expected an object");
    }
    const path = "$.draft";
    let draft;
    switch (value.kind) {
    case "addItem": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "itemId", "groupId", "presence", "rate"],
            path,
            ["metadata"],
        );
        if (object.presence !== "present") {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.presence`,
                "new items must initially be present",
            );
        }
        const rate = parseRate(object.rate, `${path}.rate`);
        if (rate.state !== "unknown") {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.rate`,
                "new items must initially have an unknown rate",
            );
        }
        draft = {
            kind: "addItem",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            itemId: itemId(object.itemId, `${path}.itemId`),
            groupId: nonEmptyString(object.groupId, `${path}.groupId`),
            presence: "present",
            rate,
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setItemPresence": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "itemId", "presence"],
            path,
            ["metadata"],
        );
        if (!PRESENCE_VALUES.has(object.presence)) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.presence`,
                "unsupported presence state",
            );
        }
        draft = {
            kind: "setItemPresence",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            itemId: itemId(object.itemId, `${path}.itemId`),
            presence: object.presence,
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setItemGroupMembership": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "itemId", "groupId", "membership"],
            path,
            ["metadata"],
        );
        if (!MEMBERSHIP_VALUES.has(object.membership)) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.membership`,
                "unsupported membership state",
            );
        }
        draft = {
            kind: "setItemGroupMembership",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            itemId: itemId(object.itemId, `${path}.itemId`),
            groupId: nonEmptyString(object.groupId, `${path}.groupId`),
            membership: object.membership,
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setGroupPresence": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "groupId", "role", "presence"],
            path,
            ["metadata"],
        );
        if (!isCalculatorGroupRoleV1(object.role)) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.role`,
                "unsupported group role",
            );
        }
        if (!PRESENCE_VALUES.has(object.presence)) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.presence`,
                "unsupported presence state",
            );
        }
        draft = {
            kind: "setGroupPresence",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            groupId: nonEmptyString(object.groupId, `${path}.groupId`),
            role: object.role,
            presence: object.presence,
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setItemRate": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "itemId", "groupId", "rate", "context"],
            path,
            ["metadata"],
        );
        const rate = parseDropRate(object.rate, `${path}.rate`);
        const context = parseRateContext(object.context, `${path}.context`);
        validateUserLootRateContext(rate, context, path);
        draft = {
            kind: "setItemRate",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            itemId: itemId(object.itemId, `${path}.itemId`),
            groupId: nonEmptyString(object.groupId, `${path}.groupId`),
            rate,
            context,
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setItemQuantity": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "itemId", "groupId", "quantity"],
            path,
            ["metadata"],
        );
        draft = {
            kind: "setItemQuantity",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            itemId: itemId(object.itemId, `${path}.itemId`),
            groupId: nonEmptyString(object.groupId, `${path}.groupId`),
            quantity: parseItemQuantity(object.quantity, `${path}.quantity`),
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setItemQuantityDefault": {
        const object = exactObject(
            value,
            ["kind", "itemId", "quantity"],
            path,
            ["metadata"],
        );
        draft = {
            kind: "setItemQuantityDefault",
            itemId: itemId(object.itemId, `${path}.itemId`),
            quantity: parseItemQuantity(object.quantity, `${path}.quantity`),
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setGroupRate": {
        const object = exactObject(
            value,
            ["kind", "fishingSource", "groupId", "rate", "context"],
            path,
            ["metadata"],
        );
        const rate = parseDropRate(object.rate, `${path}.rate`);
        const context = parseRateContext(object.context, `${path}.context`);
        validateUserLootRateContext(rate, context, path);
        draft = {
            kind: "setGroupRate",
            fishingSource: fishingSource(object.fishingSource, `${path}.fishingSource`),
            groupId: nonEmptyString(object.groupId, `${path}.groupId`),
            rate,
            context,
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setItemPrice": {
        const object = exactObject(
            value,
            ["kind", "itemId", "price"],
            path,
            ["metadata"],
        );
        draft = {
            kind: "setItemPrice",
            itemId: itemId(object.itemId, `${path}.itemId`),
            price: parseNumericValue(object.price, `${path}.price`),
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "refuteEvidence": {
        const object = exactObject(
            value,
            ["kind", "targetFactId", "reasonCode"],
            path,
            ["metadata"],
        );
        if (typeof object.targetFactId !== "string" || !FACT_ID_PATTERN.test(object.targetFactId)) {
            throw new CalculatorLayerInspectorDraftError(
                `${path}.targetFactId`,
                "expected a canonical BLAKE3 fact ID",
            );
        }
        draft = {
            kind: "refuteEvidence",
            targetFactId: object.targetFactId,
            reasonCode: nonEmptyString(object.reasonCode, `${path}.reasonCode`),
            ...(object.metadata === undefined
                ? {}
                : { metadata: parseDraftMetadata(object.metadata, `${path}.metadata`) }),
        };
        break;
    }
    case "setUserOperationMetadata": {
        const object = exactObject(
            value,
            ["kind", "operationId", "comment", "confidence"],
            path,
        );
        draft = {
            kind: "setUserOperationMetadata",
            operationId: nonEmptyString(
                object.operationId,
                `${path}.operationId`,
            ),
            comment: optionalComment(object.comment, `${path}.comment`),
            confidence: operationConfidence(
                object.confidence,
                `${path}.confidence`,
            ),
        };
        break;
    }
    case "revertUserOperation": {
        const object = exactObject(value, ["kind", "operationId"], path);
        draft = {
            kind: "revertUserOperation",
            operationId: nonEmptyString(object.operationId, `${path}.operationId`),
        };
        break;
    }
    default:
        throw new CalculatorLayerInspectorDraftError(
            `${path}.kind`,
            "unsupported semantic action",
        );
    }
    return deepFreeze(draft);
}

export function buildAddItemDraft({
    fishingSource: source,
    itemId: rawItemId,
    groupId,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "addItem",
        fishingSource: source,
        itemId: rawItemId,
        groupId,
        presence: "present",
        rate: { state: "unknown" },
        metadata,
    });
}

export function buildSetItemPresenceDraft({
    fishingSource: source,
    itemId: rawItemId,
    presence,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setItemPresence",
        fishingSource: source,
        itemId: rawItemId,
        presence,
        metadata,
    });
}

export function buildSetItemGroupMembershipDraft({
    fishingSource: source,
    itemId: rawItemId,
    groupId,
    membership,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setItemGroupMembership",
        fishingSource: source,
        itemId: rawItemId,
        groupId,
        membership,
        metadata,
    });
}

export function buildSetGroupPresenceDraft({
    fishingSource: source,
    groupId,
    role,
    presence,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setGroupPresence",
        fishingSource: source,
        groupId,
        role,
        presence,
        metadata,
    });
}

export function buildSetItemRateDraft({
    fishingSource: source,
    itemId: rawItemId,
    groupId,
    rate,
    conditions = [],
    branchId = null,
    alternative = null,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setItemRate",
        fishingSource: source,
        itemId: rawItemId,
        groupId,
        rate,
        context: { conditions, branchId, alternative },
        metadata,
    });
}

export function buildSetItemQuantityDraft({
    fishingSource: source,
    itemId: rawItemId,
    groupId,
    quantity,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setItemQuantity",
        fishingSource: source,
        itemId: rawItemId,
        groupId,
        quantity,
        metadata,
    });
}

export function buildSetItemQuantityDefaultDraft({
    itemId: rawItemId,
    quantity,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setItemQuantityDefault",
        itemId: rawItemId,
        quantity,
        metadata,
    });
}

export function buildSetGroupRateDraft({
    fishingSource: source,
    groupId,
    rate,
    conditions = [],
    branchId = null,
    alternative = null,
    metadata,
}) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setGroupRate",
        fishingSource: source,
        groupId,
        rate,
        context: { conditions, branchId, alternative },
        metadata,
    });
}

export function buildSetItemPriceDraft({ itemId: rawItemId, price, metadata }) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setItemPrice",
        itemId: rawItemId,
        price,
        metadata,
    });
}

export function buildRefuteEvidenceDraft({ targetFactId, reasonCode, metadata }) {
    return parseCalculatorLayerInspectorDraft({
        kind: "refuteEvidence",
        targetFactId,
        reasonCode,
        metadata,
    });
}

export function buildSetUserOperationMetadataDraft(
    operationId,
    comment,
    confidence,
) {
    return parseCalculatorLayerInspectorDraft({
        kind: "setUserOperationMetadata",
        operationId,
        comment,
        confidence,
    });
}

export function buildRevertUserOperationDraft(operationId) {
    return parseCalculatorLayerInspectorDraft({
        kind: "revertUserOperation",
        operationId,
    });
}

export function knownRate(value, basis = "rawWeight") {
    return deepFreeze(parseDropRate({ state: "known", value, basis }, "$.rate"));
}

export function unknownRate() {
    return Object.freeze({ state: "unknown" });
}

export function unavailableRate(reason) {
    return deepFreeze(parseRate({ state: "unavailable", reason }, "$.rate"));
}

export function knownPrice(value) {
    return deepFreeze(parseNumericValue(
        { state: "known", value, unit: "silver" },
        "$.price",
    ));
}

export function unknownPrice() {
    return Object.freeze({ state: "unknown" });
}

export function unavailablePrice(reason) {
    return deepFreeze(parseNumericValue(
        { state: "unavailable", reason },
        "$.price",
    ));
}
